import { useState, useMemo, useEffect } from 'react'
import {
  ClipboardCheck, UserCheck, UserX, Clock, Calendar,
  ChevronLeft, ChevronRight, Plus, X, Check, AlertCircle,
  TrendingUp, Users
} from 'lucide-react'
import type { AttendanceRecord, LeaveBalance, TeamMember } from '../types'
import { getAttendance, upsertAttendance, updateAttendance, getLeaveBalances } from '../services/attendance.service'
import { getTeamMembers } from '../services/team.service'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useDateRange } from '../context/DateRangeContext'
import { useIsAdmin } from '../hooks/useIsAdmin'

// ── Constants ──────────────────────────────────────────────────
const TODAY = new Date().toISOString().split('T')[0]

function getWorkingDays(): string[] {
  const days: string[] = []
  const cursor = new Date()
  // go back up to 90 calendar days to collect 60 weekdays
  cursor.setHours(12, 0, 0, 0)
  while (days.length < 60) {
    const dow = cursor.getDay()
    if (dow !== 0 && dow !== 6) {
      days.unshift(cursor.toISOString().split('T')[0])
    }
    cursor.setDate(cursor.getDate() - 1)
  }
  return days
}

const WORKING_DAYS = getWorkingDays()

const STATUS_META: Record<AttendanceRecord['status'], { label: string; color: string; bg: string; dot: string }> = {
  present:  { label: 'Present',  color: '#32c864', bg: 'rgba(50,200,100,0.12)',  dot: '#32c864' },
  late:     { label: 'Late',     color: '#ffc600', bg: 'rgba(255,198,0,0.12)',   dot: '#ffc600' },
  'half-day':{ label: 'Half Day', color: '#ff8c00', bg: 'rgba(255,140,0,0.12)',  dot: '#ff8c00' },
  absent:   { label: 'Absent',   color: '#cc3300', bg: 'rgba(204,51,0,0.12)',   dot: '#cc3300' },
  leave:    { label: 'Leave',    color: '#7c7cff', bg: 'rgba(124,124,255,0.12)', dot: '#7c7cff' },
}

function StatusBadge({ status }: { status: AttendanceRecord['status'] }) {
  const m = STATUS_META[status]
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ background: m.bg, color: m.color }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: m.dot }} />
      {m.label}
    </span>
  )
}

// ── Log Attendance Modal ───────────────────────────────────────
function LogModal({ onClose, onSave, existing, members }: {
  onClose: () => void
  onSave: (rec: Omit<AttendanceRecord, 'id'>) => void
  existing?: AttendanceRecord
  members: Array<{ id: string; name: string }>
}) {
  const defaultMember = members[0] ?? { id: '', name: '' }
  const [form, setForm] = useState({
    memberId: existing?.memberId ?? defaultMember.id,
    memberName: existing?.memberName ?? defaultMember.name,
    date: existing?.date ?? TODAY,
    checkIn: existing?.checkIn ?? '09:30',
    checkOut: existing?.checkOut ?? '18:30',
    status: existing?.status ?? ('present' as AttendanceRecord['status']),
    notes: existing?.notes ?? '',
  })

  const noTimes = form.status === 'absent' || form.status === 'leave'

  function handleMember(id: string) {
    const m = members.find(x => x.id === id)!
    setForm(f => ({ ...f, memberId: id, memberName: m.name }))
  }

  function handleSave() {
    const hrs = (() => {
      if (noTimes || !form.checkIn || !form.checkOut) return 0
      const [ih, im] = form.checkIn.split(':').map(Number)
      const [oh, om] = form.checkOut.split(':').map(Number)
      return Math.max(0, (oh * 60 + om - ih * 60 - im) / 60)
    })()
    onSave({
      memberId: form.memberId,
      memberName: form.memberName,
      date: form.date,
      checkIn: noTimes ? null : form.checkIn || null,
      checkOut: noTimes ? null : form.checkOut || null,
      status: form.status,
      hoursWorked: form.status === 'half-day' ? hrs || 4.5 : hrs,
      notes: form.notes || undefined,
    })
    onClose()
  }

  const iCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-yellow-400/50"
  const iStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.15)' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.2)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,198,0,0.1)' }}>
          <h3 className="font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            {existing ? 'Edit Attendance' : 'Log Attendance'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Member */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#a0a0a0' }}>Team Member</label>
            <select value={form.memberId} onChange={e => handleMember(e.target.value)}
              className={iCls} style={iStyle}>
              {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>

          {/* Date + Status row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#a0a0a0' }}>Date</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className={iCls} style={iStyle} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#a0a0a0' }}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as AttendanceRecord['status'] }))}
                className={iCls} style={iStyle}>
                <option value="present">Present</option>
                <option value="late">Late</option>
                <option value="half-day">Half Day</option>
                <option value="absent">Absent</option>
                <option value="leave">Leave</option>
              </select>
            </div>
          </div>

          {/* Check-in / Check-out */}
          {!noTimes && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#a0a0a0' }}>Check-in</label>
                <input type="time" value={form.checkIn ?? ''} onChange={e => setForm(f => ({ ...f, checkIn: e.target.value }))}
                  className={iCls} style={iStyle} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#a0a0a0' }}>Check-out</label>
                <input type="time" value={form.checkOut ?? ''} onChange={e => setForm(f => ({ ...f, checkOut: e.target.value }))}
                  className={iCls} style={iStyle} />
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#a0a0a0' }}>Notes <span style={{ color: '#555' }}>(optional)</span></label>
            <input type="text" value={form.notes} placeholder="e.g. Doctor appointment"
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className={iCls} style={iStyle} />
          </div>
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.1)' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#222')}
            onMouseLeave={e => (e.currentTarget.style.background = '#1a1a1a')}>
            Cancel
          </button>
          <button onClick={handleSave}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
            style={{ background: '#ffc600', color: '#000' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#e6b200')}
            onMouseLeave={e => (e.currentTarget.style.background = '#ffc600')}>
            <Check size={15} /> Save
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Today Tab ──────────────────────────────────────────────────
function TodayTab({ records, onEdit, members, isAdmin }: {
  records: AttendanceRecord[]
  onEdit: (r: AttendanceRecord) => void
  members: TeamMember[]
  isAdmin: boolean
}) {
  const todayRecs = records.filter(r => r.date === TODAY)
  const recByMember = Object.fromEntries(todayRecs.map(r => [r.memberId, r]))

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.1)' }}>
      <table className="w-full">
        <thead>
          <tr style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.08)' }}>
            {['Member', 'Role', 'Check-in', 'Check-out', 'Hours', 'Status', ''].map(h => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#555' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {members.map((m, i) => {
            const rec = recByMember[m.id]
            return (
              <tr key={m.id}
                style={{ background: i % 2 === 0 ? '#000' : 'rgba(255,255,255,0.01)', borderBottom: '1px solid rgba(255,198,0,0.05)' }}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: '#ffc600', color: '#000' }}>
                      {m.name[0]}
                    </div>
                    <span className="text-sm font-medium text-white">{m.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: '#a0a0a0' }}>{m.role}</td>
                <td className="px-4 py-3 text-sm" style={{ color: rec?.checkIn ? '#32c864' : '#555' }}>
                  {rec?.checkIn ?? '—'}
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: rec?.checkOut ? '#a0a0a0' : '#555' }}>
                  {rec?.checkOut ?? (rec ? 'In office' : '—')}
                </td>
                <td className="px-4 py-3 text-sm font-medium" style={{ color: '#ffc600' }}>
                  {rec?.hoursWorked ? `${rec.hoursWorked}h` : '—'}
                </td>
                <td className="px-4 py-3">
                  {rec ? <StatusBadge status={rec.status} /> : (
                    <span className="text-xs" style={{ color: '#555' }}>Not logged</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {rec && isAdmin && (
                    <button onClick={() => onEdit(rec)}
                      className="text-xs px-2.5 py-1 rounded-lg transition-all"
                      style={{ color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.1)' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#ffc600'; e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)' }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#a0a0a0'; e.currentTarget.style.borderColor = 'rgba(255,198,0,0.1)' }}>
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Monthly Heatmap Tab ────────────────────────────────────────
function MonthlyTab({ records, members }: { records: AttendanceRecord[]; members: TeamMember[] }) {
  const [selectedMember, setSelectedMember] = useState(members[0]?.id ?? '')
  const memberRecs = records.filter(r => r.memberId === selectedMember)
  const recByDate = Object.fromEntries(memberRecs.map(r => [r.date, r]))

  const stats = useMemo(() => {
    const total = WORKING_DAYS.length
    const present = memberRecs.filter(r => r.status === 'present').length
    const late = memberRecs.filter(r => r.status === 'late').length
    const halfDay = memberRecs.filter(r => r.status === 'half-day').length
    const absent = memberRecs.filter(r => r.status === 'absent').length
    const leave = memberRecs.filter(r => r.status === 'leave').length
    const totalHrs = memberRecs.reduce((s, r) => s + r.hoursWorked, 0)
    const pct = total > 0 ? Math.round(((present + late + halfDay * 0.5) / total) * 100) : 0
    return { total, present, late, halfDay, absent, leave, totalHrs, pct }
  }, [memberRecs])

  // Sync selectedMember when members load
  useEffect(() => {
    if (members.length > 0 && !members.find(m => m.id === selectedMember)) {
      setSelectedMember(members[0].id)
    }
  }, [members])

  const firstDay = WORKING_DAYS[0]
  const lastDay = WORKING_DAYS[WORKING_DAYS.length - 1]
  const rangeLabel = firstDay && lastDay
    ? `${new Date(firstDay).toLocaleDateString('en', { month: 'short', day: 'numeric' })} to ${new Date(lastDay).toLocaleDateString('en', { month: 'short', day: 'numeric' })}`
    : 'Last 60 working days'

  return (
    <div className="space-y-6">
      {/* Member selector */}
      <div className="flex flex-wrap gap-2">
        {members.map(m => (
          <button key={m.id} onClick={() => setSelectedMember(m.id)}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={selectedMember === m.id
              ? { background: '#ffc600', color: '#000' }
              : { background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.1)' }}>
            {m.name}
          </button>
        ))}
      </div>

      {/* Mini stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Present', val: stats.present, color: '#32c864' },
          { label: 'Late',    val: stats.late,    color: '#ffc600' },
          { label: 'Half Day',val: stats.halfDay, color: '#ff8c00' },
          { label: 'Absent',  val: stats.absent,  color: '#cc3300' },
          { label: 'Leave',   val: stats.leave,   color: '#7c7cff' },
        ].map(s => (
          <div key={s.label} className="rounded-xl px-4 py-3 text-center" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.08)' }}>
            <p className="text-xl font-bold" style={{ color: s.color, fontFamily: 'Syne, sans-serif' }}>{s.val}</p>
            <p className="text-xs mt-0.5" style={{ color: '#555' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Attendance rate bar */}
      <div className="rounded-2xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)' }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-white">Attendance Rate</span>
          <span className="text-2xl font-bold" style={{ color: '#ffc600', fontFamily: 'Syne, sans-serif' }}>{stats.pct}%</span>
        </div>
        <div className="h-3 rounded-full overflow-hidden" style={{ background: '#1a1a1a' }}>
          <div className="h-full rounded-full transition-all duration-500"
            style={{ width: `${stats.pct}%`, background: stats.pct >= 90 ? '#32c864' : stats.pct >= 75 ? '#ffc600' : '#cc3300' }} />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs" style={{ color: '#555' }}>Total hours: {stats.totalHrs.toFixed(1)}h</span>
          <span className="text-xs" style={{ color: '#555' }}>{stats.total} working days</span>
        </div>
      </div>

      {/* Calendar heatmap */}
      <div className="rounded-2xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)' }}>
        <p className="text-sm font-semibold text-white mb-4">Daily Attendance — {rangeLabel}</p>
        <div className="flex flex-wrap gap-2">
          {WORKING_DAYS.map(date => {
            const rec = recByDate[date]
            const m = rec ? STATUS_META[rec.status] : null
            const day = new Date(date).getDate()
            const month = new Date(date).toLocaleString('en', { month: 'short' })
            const isToday = date === TODAY
            return (
              <div key={date} title={rec ? `${date}: ${STATUS_META[rec.status].label}${rec.notes ? ` — ${rec.notes}` : ''}` : `${date}: Not logged`}
                className="relative group cursor-default"
                style={{
                  width: 40, height: 40,
                  borderRadius: 10,
                  background: m ? m.bg : '#1a1a1a',
                  border: isToday ? '2px solid #ffc600' : `1px solid ${m ? m.dot + '40' : 'rgba(255,198,0,0.05)'}`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                <span className="text-xs font-bold leading-none" style={{ color: m ? m.color : '#333' }}>{day}</span>
                <span style={{ fontSize: 8, color: m ? m.color + 'aa' : '#2a2a2a', lineHeight: 1.2 }}>{month}</span>
                {/* tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 pointer-events-none">
                  <div className="text-xs px-2 py-1 rounded-lg whitespace-nowrap" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)', color: '#ccc' }}>
                    {rec ? STATUS_META[rec.status].label : 'Not logged'}
                    {rec?.checkIn && ` • ${rec.checkIn}–${rec.checkOut ?? '?'}`}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t" style={{ borderColor: 'rgba(255,198,0,0.06)' }}>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ background: v.bg, border: `1px solid ${v.dot}` }} />
              <span className="text-xs" style={{ color: '#555' }}>{v.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Leave Tracker Tab ──────────────────────────────────────────
function LeaveTab({ balances }: { balances: LeaveBalance[] }) {
  function LeaveBar({ used, total, color }: { used: number; total: number; color: string }) {
    const pct = total > 0 ? (used / total) * 100 : 0
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#1a1a1a' }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
        </div>
        <span className="text-xs font-semibold w-12 text-right" style={{ color: '#a0a0a0' }}>{used}/{total}</span>
      </div>
    )
  }

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.1)' }}>
      <table className="w-full">
        <thead>
          <tr style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.08)' }}>
            {['Member', 'Casual Leave', 'Sick Leave', 'Earned Leave', 'Total Remaining'].map(h => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#555' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {balances.map((b, i) => {
            const remaining = (b.casual.total - b.casual.used) + (b.sick.total - b.sick.used) + (b.earned.total - b.earned.used)
            const totalLeave = b.casual.total + b.sick.total + b.earned.total
            const usedLeave = b.casual.used + b.sick.used + b.earned.used
            return (
              <tr key={b.memberId}
                style={{ background: i % 2 === 0 ? '#000' : 'rgba(255,255,255,0.01)', borderBottom: '1px solid rgba(255,198,0,0.05)' }}>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: '#ffc600', color: '#000' }}>
                      {b.memberName[0]}
                    </div>
                    <span className="text-sm font-medium text-white">{b.memberName}</span>
                  </div>
                </td>
                <td className="px-4 py-4 w-40">
                  <LeaveBar used={b.casual.used} total={b.casual.total} color="#ffc600" />
                </td>
                <td className="px-4 py-4 w-40">
                  <LeaveBar used={b.sick.used} total={b.sick.total} color="#cc3300" />
                </td>
                <td className="px-4 py-4 w-40">
                  <LeaveBar used={b.earned.used} total={b.earned.total} color="#7c7cff" />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold" style={{ color: remaining > 10 ? '#32c864' : remaining > 5 ? '#ffc600' : '#cc3300', fontFamily: 'Syne, sans-serif' }}>
                      {remaining}
                    </span>
                    <span className="text-xs" style={{ color: '#555' }}>/ {totalLeave - usedLeave} avail</span>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────
export default function Attendance() {
  const [records, setRecords] = useState<AttendanceRecord[]>([])
  const [members, setMembers] = useState<TeamMember[]>([])
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([])
  const [tab, setTab] = useState<'today' | 'monthly' | 'leave'>('today')
  const [showLog, setShowLog] = useState(false)
  const [editing, setEditing] = useState<AttendanceRecord | undefined>()
  const { toast } = useToast()
  const { addActivity } = useActivity()
  const { inRange } = useDateRange()
  const isAdmin = useIsAdmin()

  useEffect(() => {
    getAttendance().then(setRecords).catch(console.error)
    getTeamMembers().then(setMembers).catch(console.error)
    getLeaveBalances().then(setLeaveBalances).catch(console.error)
  }, [])

  // Date-range filtered records (today tab always shows TODAY regardless)
  const rangeRecords = records.filter(r => inRange(r.date))

  // Today's stats
  const todayRecs = records.filter(r => r.date === TODAY)
  const memberCount = members.length
  const presentToday  = todayRecs.filter(r => r.status === 'present' || r.status === 'half-day').length
  const lateToday     = todayRecs.filter(r => r.status === 'late').length
  const absentToday   = todayRecs.filter(r => r.status === 'absent').length
  const leaveToday    = todayRecs.filter(r => r.status === 'leave').length
  const notLoggedToday = memberCount - todayRecs.length

  async function handleSave(rec: Omit<AttendanceRecord, 'id'>) {
    const isNew = !editing
    try {
      if (editing) {
        await updateAttendance(editing.id, rec)
      } else {
        await upsertAttendance(rec)
      }
      const refreshed = await getAttendance()
      setRecords(refreshed)
      toast(isNew ? `Attendance logged for ${rec.memberName}` : `Attendance updated for ${rec.memberName}`, 'success')
      addActivity({ type: 'attendance', emoji: '✅', description: isNew ? `Attendance logged` : `Attendance updated`, detail: `${rec.memberName} • ${rec.date} • ${STATUS_META[rec.status].label}` })
    } catch (err) {
      console.error(err)
      toast('Failed to save attendance', 'error')
    }
    setEditing(undefined)
  }

  function openEdit(rec: AttendanceRecord) {
    setEditing(rec)
    setShowLog(true)
  }

  function openNew() {
    setEditing(undefined)
    setShowLog(true)
  }

  const tabs = [
    { id: 'today' as const,   label: "Today's Overview",  icon: UserCheck },
    { id: 'monthly' as const, label: 'Monthly View',      icon: Calendar },
    { id: 'leave' as const,   label: 'Leave Tracker',     icon: ClipboardCheck },
  ]

  const modalMembers = members.map(m => ({ id: m.id, name: m.name }))

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Attendance</h1>
          <p className="text-sm mt-0.5" style={{ color: '#555' }}>Track daily check-ins, monthly records, and leave balances</p>
        </div>
        {isAdmin && (
          <button onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all"
            style={{ background: '#ffc600', color: '#000' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#e6b200')}
            onMouseLeave={e => (e.currentTarget.style.background = '#ffc600')}>
            <Plus size={15} /> Log Attendance
          </button>
        )}
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Present Today',  val: presentToday,   icon: UserCheck,    color: '#32c864', sub: `+${lateToday} late` },
          { label: 'Late Today',     val: lateToday,      icon: Clock,        color: '#ffc600', sub: 'after 10:00' },
          { label: 'Absent Today',   val: absentToday,    icon: UserX,        color: '#cc3300', sub: 'unexcused' },
          { label: 'On Leave',       val: leaveToday,     icon: Calendar,     color: '#7c7cff', sub: 'approved' },
          { label: 'Not Logged',     val: notLoggedToday, icon: AlertCircle,  color: '#555',    sub: 'pending entry' },
        ].map(s => (
          <div key={s.label} className="rounded-2xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.08)' }}>
            <div className="flex items-center justify-between mb-2">
              <s.icon size={16} style={{ color: s.color }} />
              <span className="text-xs" style={{ color: '#555' }}>{s.sub}</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: s.color, fontFamily: 'Syne, sans-serif' }}>{s.val}</p>
            <p className="text-xs mt-0.5" style={{ color: '#555' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)', width: 'fit-content' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={tab === t.id
              ? { background: '#ffc600', color: '#000' }
              : { color: '#a0a0a0' }}
            onMouseEnter={e => { if (tab !== t.id) e.currentTarget.style.color = '#fff' }}
            onMouseLeave={e => { if (tab !== t.id) e.currentTarget.style.color = '#a0a0a0' }}>
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'today'   && <TodayTab records={records} onEdit={openEdit} members={members} isAdmin={isAdmin} />}
      {tab === 'monthly' && <MonthlyTab records={rangeRecords} members={members} />}
      {tab === 'leave'   && <LeaveTab balances={leaveBalances} />}

      {/* Modal */}
      {showLog && (
        <LogModal
          onClose={() => { setShowLog(false); setEditing(undefined) }}
          onSave={handleSave}
          existing={editing}
          members={modalMembers}
        />
      )}
    </div>
  )
}
